import { ReservationDetailDto } from '@/services/platform/api/model';
import { cn, useClientTranslation, useLocaleStore } from '@/lib/core';
import { formatAmount } from '@/lib/core/utils/currency';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { formatActivityDate } from '@/lib/core/utils/dateTime';
import { getActivityStatusColor } from '@/lib/core/utils/activityStatus';
import {
  useReservationSummaryLabel,
  useReservationSummaryValue,
} from '@/services/platform/data/booking/reservationSummary';
import { ActivityInvoiceButton } from './invoice';

interface ActivityReservationProps {
  reservation: ReservationDetailDto;
  name?: string | null;
}

export function ActivityReservation({ reservation, name }: ActivityReservationProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(state => state.locale);
  const summaryLabel = useReservationSummaryLabel();
  const summaryValue = useReservationSummaryValue();

  const summaryItems = reservation.summaryItems;
  const title = name ?? reservation.bookableServiceName ?? reservation.venue?.name;
  const receiptNumber = reservation.orders?.[0]?.orderNumber;
  const currency = reservation.orders?.[0]?.currencyCode;
  const total = reservation.orders?.reduce((sum, o) => sum + (o.totalAmount ?? 0), 0);
  const hasPriceSummary = (total ?? 0) > 0;
  // Not necessarily `orders[0]` — a reservation can span several orders and only some of them
  // produce an invoice.
  const invoiceOrder = reservation.orders?.find(o => o.invoice != null);

  return (
    <div className="flex flex-col gap-10 pb-8">
      {/* top info */}
      <div className="flex flex-col gap-8 pb-8">
        <div className="flex flex-col gap-2">
          {/* category · status */}
          <div className="flex items-center gap-2.5">
            {reservation.serviceType && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-700 shrink-0 text-[10px] font-semibold tracking-[0.5px] uppercase">
                {reservation.serviceType}
              </span>
            )}
            {reservation.serviceType && reservation.status && (
              <span className="text-jncquoi-neutral-500 text-[4px]">●</span>
            )}
            {reservation.status && (
              <span
                className={cn(
                  'font-pp-neue-montreal shrink-0 text-[10px] font-semibold tracking-[0.5px] uppercase',
                  getActivityStatusColor(reservation.status)
                )}
              >
                {reservation.status}
              </span>
            )}
          </div>
          {/* title */}
          {title && (
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 overflow-hidden text-[24px] font-semibold text-ellipsis">
              {title}
            </p>
          )}
        </div>

        {/* receipt number + booking date */}
        <div className="flex gap-6">
          {receiptNumber && (
            <div className="flex min-w-0 flex-1 flex-col gap-2 pl-4">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
                {t('App.Labels.ActivityDetailReceiptNumber')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                {receiptNumber}
              </p>
            </div>
          )}
          {reservation.startDate && (
            <div className="flex min-w-0 flex-1 flex-col gap-2 pl-4">
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] font-semibold tracking-[0.5px]">
                {t('App.Labels.ActivityDetailBookingDate')}
              </p>
              <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                {formatActivityDate(reservation.startDate, locale)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* VIEW DETAILS expandable */}
      {summaryItems && summaryItems.length > 0 && (
        <Accordion type="single" collapsible defaultValue="details">
          <AccordionItem value="details" className="border-b-[0.5px] border-[#4e4e4e]">
            <AccordionTrigger>{t('App.Labels.ActivityDetailViewDetails')}</AccordionTrigger>
            <AccordionContent className="font-pp-neue-montreal flex flex-col gap-4 pb-4 tracking-[0.5px]">
              {summaryItems.map((item, i) => {
                const isLast = i === summaryItems.length - 1;
                return (
                  <div
                    key={i}
                    className={cn(
                      'flex items-start gap-8 pb-4',
                      !isLast && 'border-jncquoi-neutral-200 border-b'
                    )}
                  >
                    <p className="text-jncquoi-neutral-1000 w-35 shrink-0 text-[12px] font-semibold uppercase">
                      {summaryLabel(item.key)}
                    </p>
                    <p className="text-jncquoi-neutral-1000 flex-1 text-right text-[14px]">
                      {summaryValue(item)}
                    </p>
                  </div>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* price summary */}
      {hasPriceSummary && (
        <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pb-4">
          <div className="flex items-center gap-4">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-[14px] font-semibold tracking-[0.5px]">
              {t('App.Labels.ActivityDetailTotal')}
            </span>
            <span className="text-jncquoi-neutral-1000 text-[16px] font-semibold tracking-[0.25px] whitespace-nowrap">
              {formatAmount(total, currency, locale)}
            </span>
          </div>
        </div>
      )}

      {/* invoice download — renders only when one of the reservation's orders carries one */}
      <ActivityInvoiceButton orderId={invoiceOrder?.publicGuid} invoice={invoiceOrder?.invoice} />
    </div>
  );
}
